/**
 * Create a same-day partner check-in queue entry (4-digit code).
 * Uses franchise-flow token against Naturals salon-admin API.
 */

import { getSalonAdminApiBaseUrl } from '../../salon-admin/utils/apiConfig';
import {
  readFranchiseFlowAuth,
  getFranchiseOrganizationId,
  getFranchiseStylistId,
  hasFranchiseFlowAuth,
  franchiseAuthHeaders,
} from '../../shared/franchiseFlowAuth';
import { fetchWithTimeout } from '../../salon/utils/fetchWithTimeout';
import {
  checkinQueueFetchInit,
  EMPTY_QUEUE,
} from '../../admin-shared/checkinQueueFetch';
import { buildHqSessionHeaders, hqSessionFetchInit } from '../../admin-shared/hqSessionAuth';
import { PORTAL_ID } from './api';

function hasPartnerSession() {
  return Boolean(localStorage.getItem('naturals_workspace_userId'));
}

function adminBase() {
  return (getSalonAdminApiBaseUrl() || '').replace(/\/$/, '');
}

function authHeaders(extra = {}) {
  return buildHqSessionHeaders(PORTAL_ID, extra);
}

function kioskFetchHeaders(extra = {}) {
  return franchiseAuthHeaders({ 'Content-Type': 'application/json', ...extra });
}

function stylistPayload(extra = {}) {
  const auth = readFranchiseFlowAuth();
  const stylistId =
    extra.stylistId ||
    extra.stylist_id ||
    getFranchiseStylistId() ||
    auth?.stylist_id ||
    auth?.login_account_id ||
    null;
  const stylistName =
    extra.stylistName ||
    extra.stylist_name ||
    auth?.full_name ||
    auth?.username ||
    null;
  return {
    stylist_id: stylistId || undefined,
    stylist_name: stylistName || undefined,
  };
}

/**
 * @param {{
 *   faceUserId?: string,
 *   customerName?: string,
 *   mobile?: string,
 *   selectedServices?: array,
 *   customerSnapshot?: object,
 *   organizationId?: string,
 *   source?: string,
 *   startService?: boolean,
 *   autoComplete?: boolean,
 *   referenceToken?: string,
 *   stylistId?: string,
 *   stylistName?: string,
 * }} payload
 */
export async function createPartnerCheckinQueue(payload = {}) {
  const base = adminBase();
  if (!base) {
    throw new Error('Salon admin API is not configured');
  }

  const orgId =
    payload.organizationId ||
    payload.organization_id ||
    getFranchiseOrganizationId() ||
    '';
  if (!orgId) {
    throw new Error('Organization is required for partner check-in');
  }

  const startService = Boolean(
    payload.startService ??
      payload.start_service ??
      payload.autoComplete ??
      payload.auto_complete,
  );

  const body = {
    organization_id: orgId,
    face_user_id: payload.faceUserId || payload.face_user_id || undefined,
    customer_name: payload.customerName || payload.customer_name || '',
    mobile: payload.mobile || '',
    selected_services: payload.selectedServices || payload.selected_services || [],
    customer_snapshot: payload.customerSnapshot || payload.customer_snapshot || {},
    source: payload.source || 'face_identity',
    start_service: startService,
    // Legacy alias for older backends
    auto_complete: startService,
    reference_token: payload.referenceToken || payload.reference_token || undefined,
    ...stylistPayload(payload),
  };

  const res = await fetchWithTimeout(`${base}/checkin-queue`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify(body),
    timeoutMs: 15000,
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok || !data?.success) {
    throw new Error(data?.error || data?.message || 'Failed to create check-in');
  }
  return data.checkin || null;
}

export async function fetchCheckinQueue({ status = 'all', preferFranchise = false } = {}) {
  const base = adminBase();
  const hasFranchise = preferFranchise && hasFranchiseFlowAuth();
  const hasSession = hasFranchise || hasPartnerSession();
  if (!base || !hasSession) {
    return { ...EMPTY_QUEUE };
  }

  const res = await fetch(
    `${base}/checkin-queue?status=${encodeURIComponent(status)}`,
    checkinQueueFetchInit(PORTAL_ID, undefined, {
      headers: kioskFetchHeaders(),
    }),
  );
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    if (res.status === 401 || res.status === 403) {
      return { ...EMPTY_QUEUE };
    }
    throw new Error(data?.error || 'Failed to load check-ins');
  }
  return data;
}

/**
 * Partner check-in analytics for the signed-in franchise (JWT organization_id).
 * @param {{ fromDate?: string, toDate?: string }} range
 */
export async function fetchCheckinAnalytics({ fromDate, toDate } = {}) {
  const base = adminBase();
  if (!base || !hasPartnerSession()) {
    throw new Error('Not authenticated');
  }

  const params = new URLSearchParams();
  if (fromDate) params.set('fromDate', fromDate);
  if (toDate) params.set('toDate', toDate);
  const qs = params.toString() ? `?${params.toString()}` : '';

  const res = await fetch(
    `${base}/checkin-queue/analytics${qs}`,
    hqSessionFetchInit(PORTAL_ID),
  );
  const data = await res.json().catch(() => ({}));
  if (!res.ok || !data?.success) {
    throw new Error(data?.error || 'Failed to load check-in analytics');
  }
  return data;
}

export async function fetchActiveVisit({
  faceUserId,
  visitReference,
  checkinId,
} = {}) {
  const base = adminBase();
  if (!base) return null;
  if (!hasFranchiseFlowAuth()) return null;

  const params = new URLSearchParams();
  if (faceUserId) params.set('faceUserId', String(faceUserId));
  if (visitReference) params.set('visitReference', String(visitReference));
  if (checkinId != null && String(checkinId).trim() !== '') {
    params.set('checkinId', String(checkinId));
  }
  if (![...params.keys()].length) return null;

  const res = await fetchWithTimeout(`${base}/checkin-queue/active-visit?${params}`, {
    method: 'GET',
    headers: kioskFetchHeaders(),
    timeoutMs: 12000,
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok || !data?.success) return null;
  return data.checkin || null;
}

export async function completeCheckinQueue(checkinId, { notes } = {}) {
  const base = adminBase();
  if (!base) throw new Error('API unavailable');

  if (!hasPartnerSession() && !hasFranchiseFlowAuth()) {
    throw new Error('Not authenticated');
  }

  const res = await fetchWithTimeout(
    `${base}/checkin-queue/${encodeURIComponent(checkinId)}/complete`,
    {
      ...hqSessionFetchInit(PORTAL_ID, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ notes: notes || undefined }),
      }),
      timeoutMs: 15000,
    },
  );
  const data = await res.json().catch(() => ({}));
  if (!res.ok || !data?.success) {
    throw new Error(data?.error || 'Failed to complete check-in');
  }
  return data.checkin;
}

export async function startServiceCheckinQueue(checkinId, extras = {}) {
  const base = adminBase();
  if (!base) throw new Error('API unavailable');
  const auth = readFranchiseFlowAuth();
  if (!hasFranchiseFlowAuth()) throw new Error('Kiosk session required');

  const res = await fetchWithTimeout(
    `${base}/checkin-queue/${encodeURIComponent(checkinId)}/start-service`,
    {
      method: 'POST',
      headers: kioskFetchHeaders(),
      body: JSON.stringify(stylistPayload(extras)),
      timeoutMs: 15000,
    },
  );
  const data = await res.json().catch(() => ({}));
  if (!res.ok || !data?.success) {
    throw new Error(data?.error || 'Failed to start service');
  }
  return data.checkin;
}

export async function linkFaceToCheckin(checkinId, { faceUserId, customerSnapshot } = {}) {
  const base = adminBase();
  if (!base) throw new Error('API unavailable');
  const auth = readFranchiseFlowAuth();
  if (!hasFranchiseFlowAuth()) throw new Error('Kiosk session required');
  if (!faceUserId) throw new Error('faceUserId is required');

  const res = await fetchWithTimeout(
    `${base}/checkin-queue/${encodeURIComponent(checkinId)}/link-face`,
    {
      method: 'POST',
      headers: kioskFetchHeaders(),
      body: JSON.stringify({
        face_user_id: faceUserId,
        customer_snapshot: customerSnapshot || undefined,
      }),
      timeoutMs: 15000,
    },
  );
  const data = await res.json().catch(() => ({}));
  if (!res.ok || !data?.success) {
    throw new Error(data?.error || 'Failed to link face profile');
  }
  return data.checkin;
}

export async function assignStylistToCheckin(checkinId, extras = {}) {
  const base = adminBase();
  if (!base) throw new Error('API unavailable');
  if (!hasPartnerSession() && !hasFranchiseFlowAuth()) {
    throw new Error('Not authenticated');
  }

  const res = await fetchWithTimeout(
    `${base}/checkin-queue/${encodeURIComponent(checkinId)}/assign-stylist`,
    {
      ...hqSessionFetchInit(PORTAL_ID, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(stylistPayload(extras)),
      }),
      timeoutMs: 15000,
    },
  );
  const data = await res.json().catch(() => ({}));
  if (!res.ok || !data?.success) {
    throw new Error(data?.error || 'Failed to assign stylist');
  }
  return data.checkin;
}

export async function cancelCheckinQueue(checkinId, { force = false } = {}) {
  const base = adminBase();
  if (!base) throw new Error('API unavailable');
  if (!hasPartnerSession() && !hasFranchiseFlowAuth()) {
    throw new Error('Not authenticated');
  }

  const res = await fetchWithTimeout(
    `${base}/checkin-queue/${encodeURIComponent(checkinId)}/cancel`,
    {
      ...hqSessionFetchInit(PORTAL_ID, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ force: Boolean(force) }),
      }),
      timeoutMs: 15000,
    },
  );
  const data = await res.json().catch(() => ({}));
  if (!res.ok || !data?.success) {
    throw new Error(data?.error || 'Failed to cancel check-in');
  }
  return data.checkin;
}

export async function reopenCheckinQueue(checkinId) {
  const base = adminBase();
  if (!base) throw new Error('API unavailable');
  if (!hasPartnerSession()) {
    throw new Error('Not authenticated');
  }

  const res = await fetchWithTimeout(
    `${base}/checkin-queue/${encodeURIComponent(checkinId)}/reopen`,
    {
      ...hqSessionFetchInit(PORTAL_ID, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: '{}',
      }),
      timeoutMs: 15000,
    },
  );
  const data = await res.json().catch(() => ({}));
  if (!res.ok || !data?.success) {
    throw new Error(data?.error || 'Failed to reopen check-in');
  }
  return data.checkin;
}

export async function lookupCheckinByCode(
  code,
  { complete = false, cancel = false, startService = false } = {},
) {
  const base = adminBase();
  if (!base) throw new Error('API unavailable');
  const auth = readFranchiseFlowAuth();
  if (!hasFranchiseFlowAuth()) throw new Error('Kiosk session required');

  const params = new URLSearchParams();
  if (cancel) params.set('cancel', '1');
  else if (complete) params.set('complete', '1');
  else if (startService) params.set('start_service', '1');
  const qs = params.toString() ? `?${params.toString()}` : '';
  const res = await fetchWithTimeout(
    `${base}/checkin-queue/by-code/${encodeURIComponent(String(code).trim())}${qs}`,
    {
      method: 'GET',
      headers: kioskFetchHeaders(),
      timeoutMs: 15000,
    },
  );
  const data = await res.json().catch(() => ({}));
  if (!res.ok || !data?.success) {
    throw new Error(data?.error || 'Invalid check-in code');
  }
  return data.checkin;
}

/** Release an active queue entry when the guest abandons Welcome (Change code / leave). */
export async function releaseCheckinByCode(code) {
  return lookupCheckinByCode(code, { complete: false, cancel: true });
}
